import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Settings as SettingsIcon,
  Users as UsersIcon,
  LogOut,
  Sun,
  Moon,
  Keyboard,
  LifeBuoy,
  Wallet as WalletIcon,
  Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human label for the license tier the org currently holds.
function licenseLabel(tier) {
  const map = {
    pro: 'Pro Lifetime License',
    standard: 'Standard Lifetime License',
    basic: 'Basic Plan',
    trial: 'Trial Plan',
  };
  if (!tier) return 'Standard Lifetime License';
  return map[String(tier).toLowerCase()]
    || `${String(tier)[0].toUpperCase()}${String(tier).slice(1)} Lifetime License`;
}

/**
 * User profile dropdown — identity card + license tier + theme toggle
 * + Keyboard Shortcuts + Help + Manage team + Settings + Logout.
 * Pure presentational; the lifecycle (open state, click-outside,
 * outbound nav, theme toggle handler) is owned by TopBar.
 */
export const ProfileMenu = React.forwardRef(function ProfileMenu(
  {
    open,
    onToggle,
    user,
    org,
    team,
    showSeats,
    isOwner,
    isDark,
    onToggleTheme,
    onOpenShortcuts,
    onOpenTeam,
    onOpenSettings,
    onHelp,
    onLogout,
  },
  ref,
) {
  const navigate = useNavigate();
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  const tierLabel = licenseLabel(org?.license_tier);
  const isPro = String(org?.license_tier || '').toLowerCase() === 'pro';
  const isSandbox = (user?.organization_id || '').startsWith('TEMP_ORG_');

  // Wallet badge — Pro tenants only (Standard has zero access to metered
  // flows so a balance is meaningless). Sandbox demo tenants return 403 from
  // /wallet — we silently skip. Live updates arrive via SSE
  // (`/api/wallet/stream`); the very first `wallet` event doubles as the
  // initial snapshot so no separate GET is needed.
  const [wallet, setWallet] = useState(null);
  useEffect(() => {
    if (!isPro || isSandbox) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const url = `${API}/wallet/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
    } catch {
      return undefined;
    }
    const handler = (ev) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        setWallet((prev) => ({
          ...(prev || {}),
          balance: data.balance,
          updated_at: data.updated_at,
          is_locked: !!data.is_locked,
        }));
      } catch {
        /* swallow — malformed frame */
      }
    };
    es.addEventListener('wallet', handler);
    // Browser retries automatically on error — no explicit handling needed.
    return () => {
      try { es.removeEventListener('wallet', handler); } catch { /* noop */ }
      try { es.close(); } catch { /* noop */ }
    };
  }, [isPro, isSandbox]);

  const openPlanOverview = () => {
    if (typeof onToggle === 'function') onToggle();
    navigate('/settings/plan-overview');
  };
  const openWallet = () => {
    if (typeof onToggle === 'function') onToggle();
    navigate('/wallet');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        data-testid="topbar-profile-btn"
        className="h-9 inline-flex items-center gap-2 pl-1 pr-2.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
      >
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: 'var(--theme-primary)' }}
        >
          {initial}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-500 dark:text-slate-300" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1.5 z-50"
          data-testid="topbar-profile-menu"
        >
          {/* identity + license */}
          <div className="px-3 py-3 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <span
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                {initial}
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate"
                  data-testid="topbar-profile-username"
                >
                  {user?.name || 'User'}
                </div>
                <button
                  type="button"
                  onClick={openPlanOverview}
                  className="text-[11px] mt-0.5 font-medium truncate hover:underline focus:outline-none text-left"
                  style={{ color: 'var(--theme-primary)' }}
                  data-testid="topbar-profile-license"
                  title="View plan details"
                >
                  {tierLabel}
                </button>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                  {user?.email}
                </div>
                {isPro && !isSandbox && wallet && (
                  <button
                    type="button"
                    onClick={openWallet}
                    data-testid="topbar-profile-wallet-badge"
                    className={`mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                      wallet.balance <= 0
                        ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
                        : wallet.balance < 10
                          ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                    }`}
                    title="Open credit wallet"
                  >
                    <WalletIcon className="w-3 h-3" />
                    <span className="tabular-nums">{Number(wallet.balance).toLocaleString('en-IN')}</span>
                    <span className="opacity-70">credits</span>
                    {wallet.balance < 10 && <Zap className="w-3 h-3 opacity-80" />}
                  </button>
                )}
              </div>
              {showSeats && (
                <span
                  data-testid="topbar-seats-badge"
                  className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border flex-shrink-0 ${
                    team.at_cap
                      ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                  }`}
                >
                  {team.seats_used}/{team.max_users}
                </span>
              )}
            </div>
          </div>

          {/* Row 2: light/dark toggle */}
          <button
            type="button"
            onClick={onToggleTheme}
            data-testid="topbar-theme-toggle-btn"
            className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
          >
            <span className="flex items-center gap-2.5">
              {isDark ? (
                <Moon className="w-4 h-4 text-slate-500 dark:text-slate-300" />
              ) : (
                <Sun className="w-4 h-4 text-slate-500" />
              )}
              <span>{isDark ? 'Dark mode' : 'Light mode'}</span>
            </span>
            <span
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                isDark ? 'bg-slate-600' : 'bg-slate-200'
              }`}
              aria-hidden="true"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  isDark ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>

          {/* Row 3: Keyboard shortcuts */}
          <button
            type="button"
            onClick={onOpenShortcuts}
            data-testid="topbar-shortcuts-btn"
            className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2.5"
          >
            <Keyboard className="w-4 h-4 text-slate-500 dark:text-slate-300" />
            <span>Keyboard Shortcuts</span>
          </button>

          {/* Row 4: Help & Support */}
          <button
            type="button"
            onClick={onHelp}
            data-testid="topbar-help-btn"
            className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2.5"
          >
            <LifeBuoy className="w-4 h-4 text-slate-500 dark:text-slate-300" />
            <span>Help &amp; Support</span>
          </button>

          {/* Manage team */}
          <button
            type="button"
            onClick={onOpenTeam}
            data-testid="topbar-team-btn"
            className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2.5"
          >
            <UsersIcon className="w-4 h-4 text-slate-500 dark:text-slate-300" />
            <span>{isOwner ? 'Manage team' : 'View team'}</span>
          </button>

          {/* Row 5: Settings */}
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid="topbar-settings-btn"
            className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2.5"
          >
            <SettingsIcon className="w-4 h-4 text-slate-500 dark:text-slate-300" />
            <span>Settings</span>
          </button>

          <div className="my-1 border-t border-slate-100 dark:border-slate-700" />

          {/* Row 6: Logout */}
          <button
            type="button"
            onClick={onLogout}
            data-testid="topbar-logout-btn"
            className="w-full text-left px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2.5"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
});

export default ProfileMenu;
